import React, { useState, useCallback, useMemo } from 'react';
import { SHeaderSortable, SPagination, SIcon } from '@avalara/skylab-sdk/react';
import _ from 'lodash';
import LANGUAGES_CONTROLLER from '../../controllers/languages';
import GenericFilterPanel from '../common/GenericFilterPanel';
import AssignCompaniesModal from './AssignCompaniesModal';
import { WILDCARD } from './constants';

const ALL_COMPANIES_GRANT = '*';
const MAX_COMPANIES_COUNT = Number.MAX_VALUE;
const MAX_ROLE_SELECTIONS = 50;

const PERMISSION_FILTER_OPTIONS = [
    { value: 'initiator', labelKey: 'DSPermissionsTable_Initiator' },
    { value: 'approver', labelKey: 'DSPermissionsTable_Approver' }
];

const getCompaniesCount = (user) => {
    if (!user.companyGrants) {
        return 0;
    }
    if (user.companyGrants.includes(ALL_COMPANIES_GRANT)) {
        return MAX_COMPANIES_COUNT;
    }
    return user.companyGrants.length;
};

const getDisplayCompanyValue = (user, languages, companiesLookup) => {
    if (user.companyGrants.includes(ALL_COMPANIES_GRANT)) {
        return LANGUAGES_CONTROLLER.format(languages, "CompanyPermissions_AllCompanies");
    }
    if (user.companiesCount === 1 && companiesLookup) {
        return companiesLookup[user.companyGrants[0]];
    }
    return LANGUAGES_CONTROLLER.format(languages, "CompanyPermissions_Companies", [user.companiesCount]);
};

const getBaseline = (user, role) => !!user.directSubmissionRoles?.[role];

const isCheckedIn = (changes, user, role) => {
    const delta = changes[user.id]?.[role];
    return delta !== undefined ? delta : getBaseline(user, role);
};

const isReadOnly = (user) => user.role?.toLowerCase().replace(/\s/g, '') === 'readonly';

const DirectSubmissionPermissionsTable = (props) => {
    const [sortingConfig, setSortingConfig] = useState({ columnId: "firstName", order: "asc" });
    const [pagingConfig, setPagingConfig] = useState({ startIndex: 0, endIndex: 9, rowsPerPage: 10 });
    const [filteringConfig, setFilteringConfig] = useState({ companies: undefined, roles: undefined, permissions: undefined, userName: undefined });
    const [permissionChanges, setPermissionChanges] = useState({});
    const [scopeAssignments, setScopeAssignments] = useState({});
    const [modalState, setModalState] = useState({ open: false, user: null });
    const [modalInitialScope, setModalInitialScope] = useState(null);
    const [applyStatus, setApplyStatus] = useState('idle');
    const [scopeLoadingUserId, setScopeLoadingUserId] = useState(null);

    const handleFilteringChange = useCallback((config) => {
        setFilteringConfig(config);
        setPagingConfig({ startIndex: 0, endIndex: 9, rowsPerPage: 10 });
    }, []);

    const { setToggleFilterPanel } = props;
    const handleDismissFilterPanel = useCallback(() => {
        setToggleFilterPanel(false);
    }, [setToggleFilterPanel]);

    const { users, languages, companiesLookup } = props;

    useMemo(() => {
        users?.forEach(user => {
            user.companiesCount = getCompaniesCount(user);
            user.displayCompanyValue = getDisplayCompanyValue(user, languages, companiesLookup);
        });
    }, [users, languages, companiesLookup]);

    const isPermissionChecked = (user, role) => isCheckedIn(permissionChanges, user, role);

    const checkedCounts = useMemo(() => {
        const counts = { initiator: 0, approver: 0 };
        (users || []).forEach(user => {
            if (isCheckedIn(permissionChanges, user, 'initiator')) counts.initiator += 1;
            if (isCheckedIn(permissionChanges, user, 'approver')) counts.approver += 1;
        });
        return counts;
    }, [users, permissionChanges]);

    const getCheckedCountForRole = (role) => checkedCounts[role] ?? 0;

    const isRoleAtLimit = (role) => getCheckedCountForRole(role) >= MAX_ROLE_SELECTIONS;

    const withRoleDelta = (state, user, role, desired) => {
        const userChanges = { ...state[user.id] };
        if (desired === getBaseline(user, role)) {
            delete userChanges[role];
        } else {
            userChanges[role] = desired;
        }
        if (Object.keys(userChanges).length === 0) {
            const rest = { ...state };
            delete rest[user.id];
            return rest;
        }
        return { ...state, [user.id]: userChanges };
    };

    const clearApplyStatus = () => setApplyStatus(prev => (prev === 'idle' ? prev : 'idle'));

    const handlePermissionChange = (user, role, checked) => {
        if (checked && !isPermissionChecked(user, role) && isRoleAtLimit(role)) return;

        clearApplyStatus();
        setPermissionChanges((prev) => withRoleDelta(prev, user, role, checked));
    };

    const isAllCheckedForRole = (role, usersList) => {
        if (!usersList?.length) return false;
        const eligible = usersList.filter(u => !isReadOnly(u));
        if (eligible.length === 0) return false;
        const allEligibleChecked = eligible.every(u => isPermissionChecked(u, role));
        if (allEligibleChecked) return true;
        return getCheckedCountForRole(role) >= MAX_ROLE_SELECTIONS;
    };

    const handleSelectAll = (role, checked, sortedUsers) => {
        const eligibleUsers = sortedUsers.filter(user => !isReadOnly(user));

        clearApplyStatus();
        setPermissionChanges((prev) => {
            let next = prev;
            if (checked) {
                const alreadyChecked = (props.users || []).filter(u => isCheckedIn(next, u, role)).length;
                const remaining = Math.max(0, MAX_ROLE_SELECTIONS - alreadyChecked);
                const toAdd = eligibleUsers.filter(u => !isCheckedIn(next, u, role));
                toAdd.slice(0, remaining).forEach(user => { next = withRoleDelta(next, user, role, true); });
            } else {
                eligibleUsers.forEach(user => { next = withRoleDelta(next, user, role, false); });
            }
            return next;
        });
    };

    const hasPermissionChanges = () => Object.keys(permissionChanges).length > 0;

    const hasScopeChanges = () => {
        return Object.keys(scopeAssignments).length > 0;
    };

    const hasUnappliedChanges = () => {
        return hasPermissionChanges() || hasScopeChanges();
    };

    const summarizeSelections = (selections) => {
        let companyCount = 0;
        let countryCount = 0;
        Object.values(selections).forEach(company => {
            const selected = Object.values(company.countries).filter(Boolean).length;
            if (selected > 0) {
                companyCount += 1;
                countryCount += selected;
            }
        });
        return { companyCount, countryCount };
    };

    const summarizePairs = (pairs) => {
        const companies = new Set(pairs.map(pair => pair.companyId));
        const countryCount = pairs.filter(pair => pair.countryCode !== WILDCARD).length;
        return { companyCount: companies.size, countryCount };
    };

    const getResolvedScope = (user) => {
        if (scopeAssignments[user.id]) return { kind: 'selections', value: scopeAssignments[user.id] };
        if (user.appliedScope) return { kind: 'selections', value: user.appliedScope };
        if (user.dsScope && !user.dsScope.isDefault && user.dsScope.pairs?.length) {
            return { kind: 'pairs', value: user.dsScope };
        }
        return null;
    };

    const isScopeRestricted = (scope) => {
        if (!scope) return false;
        const summary = scope.kind === 'selections'
            ? summarizeSelections(scope.value)
            : summarizePairs(scope.value.pairs);
        return summary.companyCount > 0;
    };

    const hasRestrictedScope = (user) => isScopeRestricted(getResolvedScope(user));

    const buildUserCompanies = useCallback((user) => {
        if (!user?.companyGrants?.length || !props.allCompanies?.length) return [];

        const hasAllAccess = user.companyGrants.includes(ALL_COMPANIES_GRANT);
        const accessibleCompanyIds = hasAllAccess
            ? props.allCompanies.map(c => c.id.toString())
            : user.companyGrants;

        return accessibleCompanyIds
            .map(companyId => {
                const companyInfo = props.allCompanies.find(c => c.id.toString() === companyId.toString());
                if (!companyInfo) return null;
                return {
                    id: companyId,
                    name: `${companyInfo.code} - ${companyInfo.name}`
                };
            })
            .filter(Boolean);
    }, [props.allCompanies]);

    const modalUserCompanies = useMemo(() => {
        if (!modalState.user) return [];
        return buildUserCompanies(modalState.user);
    }, [modalState.user, buildUserCompanies]);

    const resolveInitialScope = async (user) => {
        if (scopeAssignments[user.id]) return { kind: 'selections', value: scopeAssignments[user.id] };
        if (user.appliedScope) return { kind: 'selections', value: user.appliedScope };
        if (user.dsScope) return { kind: 'pairs', value: user.dsScope };
        if (props.onGetUserDsScope && user.iamUserId) {
            const scope = await props.onGetUserDsScope(user.iamUserId);
            return { kind: 'pairs', value: scope };
        }
        return null;
    };

    const handleOpenScopeModal = async (user) => {
        if (scopeLoadingUserId) return;
        setScopeLoadingUserId(user.id);
        try {
            const initialScope = await resolveInitialScope(user);
            setModalInitialScope(initialScope);
            setModalState({ open: true, user });
        } finally {
            setScopeLoadingUserId(null);
        }
    };

    const handleCloseScopeModal = () => {
        setModalState({ open: false, user: null });
        setModalInitialScope(null);
    };

    const handleApplyScope = (userId, selections) => {
        clearApplyStatus();
        setScopeAssignments(prev => ({ ...prev, [userId]: selections }));
        handleCloseScopeModal();
    };

    const handleApplyChanges = async () => {
        if (!hasUnappliedChanges() || applyStatus === 'applying') return;
        setApplyStatus('applying');
        try {
            await props.onApplyChanges(permissionChanges, scopeAssignments);
            setPermissionChanges({});
            setScopeAssignments({});
            setPagingConfig({ startIndex: 0, endIndex: 9, rowsPerPage: 10 });
            setApplyStatus('success');
        } catch {
            setApplyStatus('error');
        }
    };

    const onSort = (e) => {
        const columnId = e.detail.id;
        const order = e.detail.ascending ? "asc" : "desc";
        if (["id", "firstName", "displayCompanyValue", "role"].includes(columnId)) {
            setSortingConfig({ columnId, order });
        }
    };

    const onPaginate = (e) => {
        const startIndex = e.detail.startIndex;
        const endIndex = e.detail.endIndex;
        const rowsPerPage = e.detail.rowsPerPage;
        setPagingConfig({ startIndex, endIndex, rowsPerPage });
    };

    const getFilteredUsers = () => {
        const filterByCompany = (users) => {
            if (!filteringConfig.companies?.length) {
                return users;
            }
            return users.filter((user) => {
                return user.companyGrants.includes(ALL_COMPANIES_GRANT) || _.intersection(user.companyGrants, filteringConfig.companies).length > 0;
            });
        };

        const filterByRole = (users) => {
            if (!filteringConfig.roles?.length) {
                return users;
            }
            return users.filter((user) => filteringConfig.roles.includes(user.role));
        };

        const hasAnyPermission = (user) => {
            return filteringConfig.permissions.some(
                (permission) => getBaseline(user, permission)
            );
        };

        const filterByPermission = (users) => {
            if (!filteringConfig.permissions?.length) return users;
            return users.filter(hasAnyPermission);
        };

        const filterByUserName = (users) => {
            if (!filteringConfig.userName) {
                return users;
            }
            const searchString = filteringConfig.userName.toLowerCase();
            return users.filter((user) => {
                const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                return fullName.toLowerCase().includes(searchString);
            });
        };

        if (!props?.users) {
            return [];
        }

        const byCompany = filterByCompany(props.users);
        const byRole = filterByRole(byCompany);
        const byPermission = filterByPermission(byRole);
        return filterByUserName(byPermission);
    };

    const sortUsers = (users) => {
        if (!sortingConfig.columnId) return users;
        if (sortingConfig.columnId === "displayCompanyValue") {
            return _.orderBy(users, ["companiesCount", sortingConfig.columnId], [sortingConfig.order, sortingConfig.order]);
        }
        return _.orderBy(users, [sortingConfig.columnId], [sortingConfig.order]);
    };

    const renderScopeButton = (user, labelKey) => (
        <button
            className="secondary mt0 mb0 small-font"
            onClick={() => handleOpenScopeModal(user)}
            disabled={scopeLoadingUserId === user.id}
            data-testid={`scope-button-${user.id}`}
        >
            {LANGUAGES_CONTROLLER.format(props.languages, labelKey)}
        </button>
    );

    const renderScopeColumn = (user, isReadOnlyUser, initiatorChecked, approverChecked) => {
        const hasPermission = initiatorChecked || approverChecked;

        if (isReadOnlyUser || !hasPermission) {
            return <span className="small-font">{LANGUAGES_CONTROLLER.format(props.languages, 'DSPermissions_ScopeNotAssigned')}</span>;
        }

        const restricted = hasRestrictedScope(user);
        const summaryLabel = LANGUAGES_CONTROLLER.format(
            props.languages,
            restricted ? 'DSPermissions_RestrictedAccess' : 'DSPermissions_DefaultAccess'
        );

        return (
            <>
                <span className="small-font mr10">{summaryLabel}</span>
                {renderScopeButton(user, restricted ? 'DSPermissions_EditScope' : 'DSPermissions_RestrictScope')}
            </>
        );
    };

    const generatePageRows = (sortedUsers) => {
        if (!sortedUsers?.length) return null;

        const initiatorAtLimit = isRoleAtLimit('initiator');
        const approverAtLimit = isRoleAtLimit('approver');
        const limitTooltip = LANGUAGES_CONTROLLER.format(props.languages, 'DSPermissions_SelectionLimitTooltip', [MAX_ROLE_SELECTIONS]);
        const readOnlyTooltip = LANGUAGES_CONTROLLER.format(props.languages, 'DSPermissions_ReadOnlyUserTooltip');

        const currentPage = sortedUsers.slice(pagingConfig.startIndex, pagingConfig.endIndex + 1);
        return currentPage.map(user => {
            const isReadOnlyUser = isReadOnly(user);
            const initiatorChecked = isPermissionChecked(user, 'initiator');
            const approverChecked = isPermissionChecked(user, 'approver');
            const initiatorDisabled = isReadOnlyUser || (!initiatorChecked && initiatorAtLimit);
            const approverDisabled = isReadOnlyUser || (!approverChecked && approverAtLimit);
            
            let initiatorTooltip;
            let approverTooltip;
            
            if (isReadOnlyUser) {
                initiatorTooltip = readOnlyTooltip;
                approverTooltip = readOnlyTooltip;
            } else if (initiatorDisabled) {
                initiatorTooltip = limitTooltip;
            }
            
            if (!isReadOnlyUser && approverDisabled) {
                approverTooltip = limitTooltip;
            }

            return (
                <tr key={user.id}>
                    <td className="twenty-width">
                        <div className="bold">{user.firstName} {user.lastName}</div>
                        <div className="small-font">{user.id}</div>
                    </td>
                    <td className="twenty-width">{user.role}</td>
                    <td className="text-center" title={initiatorTooltip}>
                        <fieldset>
                            <input
                                type="checkbox"
                                id={`initiator-${user.id}`}
                                checked={initiatorChecked}
                                disabled={initiatorDisabled}
                                onChange={(e) => handlePermissionChange(user, 'initiator', e.target.checked)}
                                data-testid={`initiator-checkbox-${user.id}`}
                            />
                            <label className="checkbox" htmlFor={`initiator-${user.id}`}>
                                <span className="visually-hidden">Initiator</span>
                            </label>
                        </fieldset>
                    </td>
                    <td className="text-center" title={approverTooltip}>
                        <fieldset>
                            <input
                                type="checkbox"
                                id={`approver-${user.id}`}
                                checked={approverChecked}
                                disabled={approverDisabled}
                                onChange={(e) => handlePermissionChange(user, 'approver', e.target.checked)}
                                data-testid={`approver-checkbox-${user.id}`}
                            />
                            <label className="checkbox" htmlFor={`approver-${user.id}`}>
                                <span className="visually-hidden">Approver</span>
                            </label>
                        </fieldset>
                    </td>
                    <td>
                        {user.displayCompanyValue}
                    </td>
                    <td>
                        {renderScopeColumn(user, isReadOnlyUser, initiatorChecked, approverChecked)}
                    </td>
                </tr>
            );
        });
    };

    const filterPanel = useMemo(() => (
        <GenericFilterPanel
            open={props.toggleFilterPanel}
            onDismiss={handleDismissFilterPanel}
            allCompanyOptions={props.allCompanyOptions}
            setFilteringConfig={handleFilteringChange}
            filterPrefix="DSPermissionsFilter"
            showPermissionsFilter={true}
            permissionOptions={PERMISSION_FILTER_OPTIONS}
        />
    ), [props.toggleFilterPanel, props.allCompanyOptions, handleDismissFilterPanel, handleFilteringChange]);

    const filteredUsers = getFilteredUsers();
    const sortedFilteredUsers = sortUsers(filteredUsers);
    const usersRows = generatePageRows(sortedFilteredUsers);

    return (
        <div>
            {filterPanel}
            <s-table-container data-testid="ds-perm-table" tabindex="0">
                <table>
                    <thead>
                        <tr>
                            <th data-testid="name-column" className="pb0 pt4">
                                <SHeaderSortable
                                    id="firstName"
                                    text={LANGUAGES_CONTROLLER.format(props.languages, "DSPermissionsTable_Name")}
                                    onS-sort={onSort}
                                />
                            </th>
                            <th data-testid="role-column" className="pb0 pt4">
                                <SHeaderSortable
                                    id="role"
                                    text={LANGUAGES_CONTROLLER.format(props.languages, "DSPermissionsTable_Role")}
                                    onS-sort={onSort}
                                />
                            </th>
                            <th data-testid="initiator-column" className="fifteen-width text-center pb0 pt4" title={LANGUAGES_CONTROLLER.format(props.languages, 'DSPermissions_SelectAllLimitHint', [MAX_ROLE_SELECTIONS])}>
                                <div className="ds-permissions-select-all">
                                    <SIcon name="info-circle" className="ds-permissions-info-icon" data-testid="initiator-limit-info" />
                                    {LANGUAGES_CONTROLLER.format(props.languages, "DSPermissionsTable_Initiator")}
                                </div>
                                <fieldset className="ds-permissions-select-all">
                                    <input
                                        type="checkbox"
                                        id="select-all-initiator"
                                        checked={isAllCheckedForRole('initiator', sortedFilteredUsers)}
                                        onChange={(e) => handleSelectAll('initiator', e.target.checked, sortedFilteredUsers)}
                                        data-testid="select-all-initiator"
                                    />
                                    <label className="checkbox" htmlFor="select-all-initiator">
                                        <span className="visually-hidden">Select all initiators</span>
                                    </label>
                                </fieldset>
                            </th>
                            <th data-testid="approver-column" className="text-center pb0 pt4" title={LANGUAGES_CONTROLLER.format(props.languages, 'DSPermissions_SelectAllLimitHint', [MAX_ROLE_SELECTIONS])}>
                                <div className="ds-permissions-select-all">
                                    <SIcon name="info-circle" className="ds-permissions-info-icon" data-testid="approver-limit-info" />
                                    {LANGUAGES_CONTROLLER.format(props.languages, "DSPermissionsTable_Approver")}
                                </div>
                                <fieldset className="ds-permissions-select-all">
                                    <input
                                        type="checkbox"
                                        id="select-all-approver"
                                        checked={isAllCheckedForRole('approver', sortedFilteredUsers)}
                                        onChange={(e) => handleSelectAll('approver', e.target.checked, sortedFilteredUsers)}
                                        data-testid="select-all-approver"
                                    />
                                    <label className="checkbox" htmlFor="select-all-approver">
                                        <span className="visually-hidden">Select all approvers</span>
                                    </label>
                                </fieldset>
                            </th>
                            <th data-testid="company-column" className="pb0 pt4">
                                <SHeaderSortable
                                    id="displayCompanyValue"
                                    text={LANGUAGES_CONTROLLER.format(props.languages, "DSPermissionsTable_Company")}
                                    onS-sort={onSort}
                                />
                            </th>
                            <th data-testid="scope-column" className="pb0 pt4">
                                {LANGUAGES_CONTROLLER.format(props.languages, "DSPermissionsTable_AssignedScope")}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {usersRows}
                    </tbody>
                </table>
            </s-table-container>

            <div className="mt20 mb20">
                <SPagination
                    totalRecords={sortedFilteredUsers.length}
                    startIndex={pagingConfig.startIndex}
                    rowsPerPage={pagingConfig.rowsPerPage}
                    onS-paginate={onPaginate}
                />
            </div>

            <div className="mt20 mb170 text-right">
                {applyStatus === 'error' && (
                    <span className="error-text mr15" data-testid="apply-error-message">
                        {LANGUAGES_CONTROLLER.format(props.languages, "DSPermissions_ApplyError")}
                    </span>
                )}
                <button
                    className="primary"
                    disabled={!hasUnappliedChanges() || applyStatus === 'applying'}
                    onClick={handleApplyChanges}
                    data-testid="apply-changes-button"
                >
                    {applyStatus === 'applying'
                        ? LANGUAGES_CONTROLLER.format(props.languages, "DSPermissions_Applying")
                        : LANGUAGES_CONTROLLER.format(props.languages, "DSPermissions_ApplyChanges")}
                </button>
            </div>

            <AssignCompaniesModal
                open={modalState.open}
                onClose={handleCloseScopeModal}
                onApply={handleApplyScope}
                user={modalState.user}
                userCompanies={modalUserCompanies}
                onLoadCompanyVatNumbers={props.onLoadCompanyVatNumbers}
                companyVatNumbers={props.companyVatNumbers}
                initialScope={modalInitialScope}
                languages={props.languages}
            />
        </div>
    );
};


export default DirectSubmissionPermissionsTable;
